import Dexie, { type Table } from 'dexie';
import type {
  Assignment,
  CityRecord,
  EquipmentRecord,
  LogEntry,
  MediaQueueItem,
} from '@/types/domain';

/**
 * Generic key/value row used for sync metadata, feature flags, etc.
 */
export interface MetaRow {
  key: string;
  value: string;
}

/**
 * Dexie schema mirroring the Drift tables in the native app.
 *
 * IMPORTANT: `assignments.devF31SwitchUpd` is the local-side switch.
 * On sync upserts we MUST preserve it (see `upsertAssignmentPreservingLocal`).
 */
export class AppDatabase extends Dexie {
  assignments!: Table<Assignment, string>;
  cities!: Table<CityRecord, string>;
  equipment!: Table<EquipmentRecord, string>;
  logQueue!: Table<LogEntry, string>;
  mediaQueue!: Table<MediaQueueItem, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('director_tms_driver');

    this.version(1).stores({
      assignments:
        'remF04Id, remF31Switch, devF31SwitchUpd, remModifiedTimestamp, remDispatchType',
      cities: 'id, name',
      equipment: 'id',
      logQueue: 'id, status, capturedAt, assignmentId, logType',
      mediaQueue: 'id, status, capturedAt, assignmentId, fmRecordId, kind',
      meta: 'key',
    });

    // v2: index logClientId for the two-step media flow (resolve fmRecordId
    // from the parent log once it has synced).
    this.version(2).stores({
      mediaQueue:
        'id, status, capturedAt, assignmentId, fmRecordId, logClientId, kind',
    });
  }

  /**
   * Upsert an assignment from the server while preserving the local-side
   * switch (`devF31SwitchUpd`). This fixes the "sync clobber" bug observed
   * in the native app.
   *
   * EXCEPTION (Martin, re-dispatch scenarios): an incoming record with
   * rem_f31_switch == 0 is a FRESH dispatch. Dispatch resets the same
   * rem_f04_ID record to 0 when re-dispatching after a dead call, a dock
   * rejection, or a pickup-cancelled-then-redispatched-as-delivery change.
   * In that case the server wins completely: all fields (including
   * DEV_f200_disp_switch, which routes the call to the right list/tab) are
   * restored from the server and the local switch resets to 1 so the call
   * reappears for the second attempt. Without this, a stale local 99
   * (dead-called/complete) was preserved and the re-dispatched call showed
   * on no list.
   */
  async upsertAssignmentPreservingLocal(incoming: Assignment): Promise<void> {
    await this.transaction('rw', this.assignments, async () => {
      const existing = await this.assignments.get(incoming.remF04Id);

      if (incoming.remF31Switch === '0') {
        // Fresh (re-)dispatch: rebuild wholesale from the server record.
        await this.assignments.put({
          ...incoming,
          devF31SwitchUpd: 1,
          receivedAt: new Date().toISOString(),
          syncedAt: new Date().toISOString(),
        });
        return;
      }

      const merged: Assignment = {
        ...incoming,
        devF31SwitchUpd:
          existing?.devF31SwitchUpd !== undefined && existing.devF31SwitchUpd > 1
            ? existing.devF31SwitchUpd
            : incoming.devF31SwitchUpd,
        receivedAt: existing?.receivedAt ?? incoming.receivedAt ?? new Date().toISOString(),
        syncedAt: new Date().toISOString(),
      };
      await this.assignments.put(merged);
    });
  }

  async upsertAssignmentsPreservingLocal(rows: Assignment[]): Promise<void> {
    if (!rows.length) return;
    await this.transaction('rw', this.assignments, async () => {
      for (const row of rows) {
        await this.upsertAssignmentPreservingLocal(row);
      }
    });
  }

  /**
   * Purge fully completed assignments (local switch === 99).
   */
  async purgeCompleted(): Promise<number> {
    return this.assignments.where('devF31SwitchUpd').equals(99).delete();
  }

  async getMeta(key: string): Promise<string | undefined> {
    const row = await this.meta.get(key);
    return row?.value;
  }

  async setMeta(key: string, value: string): Promise<void> {
    await this.meta.put({ key, value });
  }
}

let _db: AppDatabase | null = null;

export function getDb(): AppDatabase {
  if (!_db) {
    _db = new AppDatabase();
  }
  return _db;
}

/**
 * Test-only helper: replace the database singleton (e.g. with a fresh
 * Dexie instance backed by fake-indexeddb) and return it.
 */
export function __setDbForTests(db: AppDatabase | null): void {
  _db = db;
}
